import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "../../firebase";
import Window from "../../components/Window";
import Icon, { IconTile } from "../../components/Icon";
import { Avatar, SpoonMeter } from "../../components/ProfileBits";
import { useBlocks } from "../../blocks";
import { FlagRow } from "../../components/Flags";
import { flagInfo, normalizeFlags } from "../../flags";

interface User {
  id: string;
  username?: string;
  name?: string;
  age?: string;
  location?: string;
  pronouns?: string;
  hobbies?: string;
  avatar?: string;
  avatarTone?: string;
  avatarConfig?: unknown;
  spoons?: number;
  flags?: unknown;
  visibility?: {
    name?: boolean;
    age?: boolean;
    location?: boolean;
    pronouns?: boolean;
    showOnBoard?: boolean;
  };
}

export default function PenPalDirectory() {
  const [allUsers, setUsers] = useState<User[]>([]);
  const { ids: blocked } = useBlocks();
  const listed = allUsers.filter((u) => !blocked.has(u.id));
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [flag, setFlag] = useState("");

  // Search only what each person chose to show.
  const term = search.trim().toLowerCase();
  const searchable = (u: User) => {
    const show = u.visibility || {};
    return [
      u.username,
      show.name && u.name,
      show.pronouns && u.pronouns,
      show.location && u.location,
      u.hobbies,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
  };
  const users = listed.filter(
    (u) =>
      (!flag || normalizeFlags(u.flags).includes(flag)) &&
      (!term || searchable(u).includes(term))
  );
  // Only offer flags someone in the directory actually shows.
  const flagOptions = Array.from(new Set(listed.flatMap((u) => normalizeFlags(u.flags))))
    .map((id) => ({ id, name: flagInfo(id)?.name || id }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const filtering = !!term || !!flag;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, "users"));
        const filtered = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() } as User))
          .filter(
            (user) =>
              user.visibility?.showOnBoard && user.id !== auth.currentUser?.uid
          );
        setUsers(filtered);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  return (
    <>
      <Window title="directory.htm">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="window-heading flex items-center gap-3">
              <IconTile name="book" tone="violet" size={44} /> Pen Pal Directory
            </h1>
            <p className="window-sub !mb-0">
              Everyone who chose to be listed. Click a card to say hi.
            </p>
          </div>
          <Link to="/board" className="btn btn-plain btn-sm">
            <Icon name="back" size={16} /> Back to the board
          </Link>
        </div>
      </Window>

      {!loading && listed.length > 0 && (
        <div className="flex flex-wrap items-end gap-3" role="search">
          <label className="field min-w-[12rem] flex-1">
            <span className="field-label">Search pen pals</span>
            <input
              type="search"
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="a hobby, a place, pronouns, a username..."
            />
          </label>
          {flagOptions.length > 0 && (
            <label className="field min-w-[12rem]">
              <span className="field-label">Shows this flag</span>
              <select className="input" value={flag} onChange={(e) => setFlag(e.target.value)}>
                <option value="">Any flag</option>
                {flagOptions.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {filtering && (
            <button
              type="button"
              className="btn btn-plain btn-sm"
              onClick={() => {
                setSearch("");
                setFlag("");
              }}
            >
              Clear
            </button>
          )}
          {filtering && (
            <p className="w-full text-sm text-ink-soft" role="status">
              {users.length} of {listed.length} pen pals
            </p>
          )}
        </div>
      )}

      {loading ? (
        <p className="empty">Flipping through the address book...</p>
      ) : users.length === 0 ? (
        <div className="window">
          <p className="empty">
            <span className="empty-icon">
              <Icon name="community" size={36} tone="blue" pop />
            </span>
            {filtering ? "Nobody matches that yet. Try another word?" : "No profiles to show right now."}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {users.map((user) => {
            // Only show the details each person chose to make visible.
            const show: NonNullable<User["visibility"]> = user.visibility || {};
            const details = [
              show.pronouns && user.pronouns,
              show.age && user.age && `${user.age}`,
              show.location && user.location,
            ].filter(Boolean);
            return (
              <Link
                key={user.id}
                to={`/profile/${user.id}`}
                className="profile-box block text-ink no-underline shadow-[3px_3px_0_rgba(74,52,38,0.28)] hover:text-ink"
              >
                <div className="profile-box-head">
                  @{user.username || "penpal"}
                </div>
                <div className="profile-box-body flex gap-3">
                  <Avatar
                    config={user.avatarConfig}
                    icon={user.avatar}
                    tone={user.avatarTone}
                    size={52}
                  />
                  <div className="min-w-0">
                    <p className="font-display text-lg font-semibold">
                      {(show.name && user.name) || `@${user.username || "penpal"}`}
                    </p>
                    {details.length > 0 && (
                      <p className="text-sm text-ink-soft">{details.join(" · ")}</p>
                    )}
                    {normalizeFlags(user.flags).length > 0 && (
                      <div className="mt-1">
                        <FlagRow ids={normalizeFlags(user.flags)} height={14} />
                      </div>
                    )}
                    {user.hobbies && (
                      <p className="mt-1 text-sm">
                        {user.hobbies.length > 90
                          ? user.hobbies.slice(0, 90) + "…"
                          : user.hobbies}
                      </p>
                    )}
                    {user.spoons ? (
                      <div className="mt-2">
                        <SpoonMeter spoons={user.spoons} />
                      </div>
                    ) : null}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
